

import { 
  SystemEntity, 
  ResourceEntity, 
  ResourcePlanEntity, 
  ResourceKind,
  HierarchyItem, 
  QueryParams, 
  QueryResult, 
  IRepositories 
} from '../types';

// --- Data Helpers ---

const mockMemberships = [
    { groupName: "GlobalAdmins", nodeName: "Root", role: "Owner" },
    { groupName: "LocalOps", nodeName: "Berlin", role: "Operator" }
];

const mockConfig = {
    paramA: "value1",
    syncEnabled: true
};

// --- UUID Constants ---

const SYS_MAIN = "7a2e0fa0-d84f-81c8-387c-cba962518d94";
const SYS_INTERNAL = "10000000-0000-0000-0000-000000000001";
const SYS_IOT = "10000000-0000-0000-0000-000000000002";
const SYS_LEGACY = "10000000-0000-0000-0000-000000000003";
const SYS_CLOUD = "10000000-0000-0000-0000-000000000004";

// --- Data Store ---

const SYSTEMS: SystemEntity[] = [
  { 
      id: SYS_MAIN, 
      name: "Main Access Control",
      memberships: mockMemberships,
      resources: [],
      resourcePlans: []
  },
  { 
      id: SYS_INTERNAL, 
      name: "Internal Systems",
      memberships: [],
      resources: [],
      resourcePlans: []
  },
  { 
      id: SYS_IOT, 
      name: "IoT Devices",
      memberships: [],
      resources: [],
      resourcePlans: []
  },
  { 
      id: SYS_LEGACY, 
      name: "Legacy Controller A",
      memberships: [],
      resources: [],
      resourcePlans: []
  },
  { 
      id: SYS_CLOUD, 
      name: "Cloud Access Manager",
      memberships: mockMemberships,
      resources: [],
      resourcePlans: []
  }
];

const RESOURCE_DEFS: ResourceEntity[] = [
  // Existing Resources (Preserving IDs from INITIAL_DATA where possible)
  { id: "fabd7570-34c7-141c-4aac-ffcf1e4e5bbe", name: "Main Entrance Lobby", description: "Primary public entrance", referenceId: "EXT-001", resourceKind: ResourceKind.Single, resourceType: "Door", memberships: [], configuration: mockConfig, systemId: SYS_MAIN },
  { id: "fabd7570-34c7-141c-4aac-ffcf1e4e5bbf", name: "Side Entrance Staff", description: "Staff only entrance", referenceId: "EXT-002", resourceKind: ResourceKind.Single, resourceType: "Door", memberships: [], configuration: mockConfig, systemId: SYS_MAIN },
  
  // Mapped to Internal
  { id: "fabd7570-34c7-141c-4aac-ffcf1e4e5bbb", name: "Office 101 (North)", description: "Open space area north", referenceId: "INT-101", resourceKind: ResourceKind.Collection, resourceType: "Zone", memberships: mockMemberships, configuration: mockConfig, systemId: SYS_INTERNAL },
  { id: "fabd7570-34c7-141c-4aac-ffcf1e4e5bbc", name: "Office 102 (North)", description: "Meeting rooms north", referenceId: "INT-102", resourceKind: ResourceKind.Collection, resourceType: "Zone", memberships: [], configuration: mockConfig, systemId: SYS_INTERNAL },
  { id: "fabd7570-34c7-141c-4aac-ffcf1e4e5bbd", name: "Office 103 (East)", description: "Quiet zone east", referenceId: "INT-103", resourceKind: ResourceKind.Collection, resourceType: "Zone", memberships: [], configuration: mockConfig, systemId: SYS_INTERNAL },
  
  // Mapped to Cloud
  { id: "899d0c8b-9ec8-aa62-3956-b770f75dd7ea", name: "Executive Suite", description: "High security area", referenceId: "SEC-900", resourceKind: ResourceKind.Fixed, resourceType: "SecureZone", memberships: mockMemberships, configuration: mockConfig, systemId: SYS_CLOUD },
  { id: "30000000-0000-0000-0000-000000000001", name: "Conference Room A", description: "Large conference room", referenceId: "CONF-A", resourceKind: ResourceKind.Single, resourceType: "Room", memberships: [], configuration: mockConfig, systemId: SYS_CLOUD },
  { id: "30000000-0000-0000-0000-000000000002", name: "Conference Room B", description: "Small meeting room", referenceId: "CONF-B", resourceKind: ResourceKind.Single, resourceType: "Room", memberships: [], configuration: mockConfig, systemId: SYS_CLOUD },

  // Mapped to Legacy
  { id: "a118c70c-0944-dc75-db81-26665a7ff674", name: "Server Room Rack A", description: "Main server rack access", referenceId: "SRV-A", resourceKind: ResourceKind.Single, resourceType: "Cabinet", memberships: [], configuration: mockConfig, systemId: SYS_LEGACY },
  { id: "30000000-0000-0000-0000-000000000003", name: "Cafeteria Turnstile", description: "Lunch area access", referenceId: "CAF-01", resourceKind: ResourceKind.Single, resourceType: "Turnstile", memberships: [], configuration: mockConfig, systemId: SYS_LEGACY },

  // Mapped to IoT
  { id: "fabd7570-34c7-141c-4aac-ffcf1e4e5bba", name: "Printer Room", description: "Common service area", referenceId: "SVC-001", resourceKind: ResourceKind.Single, resourceType: "Room", memberships: [], configuration: mockConfig, systemId: SYS_IOT },
];

// Generate bulk resources with UUIDs
for (let i = 1; i <= 60; i++) {
    const floor = Math.floor((i - 1) / 15) + 1;
    const sys = SYSTEMS[i % SYSTEMS.length];
    // Create a fake UUID based on index
    const uuid = `50000000-0000-0000-0000-${(100000000000 + i).toString()}`;
    
    RESOURCE_DEFS.push({ 
        id: uuid,
        name: `Floor ${floor} - Desk ${i}`, 
        description: `Hot desk on floor ${floor}`,
        referenceId: `DSK-${floor}-${i}`,
        resourceKind: ResourceKind.Single,
        resourceType: "Desk",
        memberships: [],
        configuration: {},
        systemId: sys.id 
    });
}

const PLANS: ResourcePlanEntity[] = [
  // Moved to Main Access Control to fix missing plans issue
  { id: "3430d084-b147-a576-0018-3a3726ecb4d7", name: "Standard Access Plan", description: "9-5 Access", referenceId: "PLN-STD", priority: 10, memberships: [], configuration: mockConfig, systemId: SYS_MAIN },
  { id: "20000000-0000-0000-0000-000000000005", name: "Emergency Access", description: "Override all locks", referenceId: "PLN-EMG", priority: 100, memberships: [], configuration: mockConfig, systemId: SYS_MAIN },
  
  // Cloud Plans
  { id: "20000000-0000-0000-0000-000000000001", name: "24/7 Unlimited", description: "All hours access", referenceId: "PLN-ALL", priority: 99, memberships: mockMemberships, configuration: mockConfig, systemId: SYS_CLOUD },
  
  // Internal Plans
  { id: "20000000-0000-0000-0000-000000000002", name: "Working Hours (08:00 - 18:00)", description: "Standard business hours", referenceId: "PLN-BIZ", priority: 5, memberships: [], configuration: {}, systemId: SYS_INTERNAL },
  
  // Legacy Plans
  { id: "20000000-0000-0000-0000-000000000003", name: "Visitor Restricted", description: "Escort required", referenceId: "PLN-VIS", priority: 0, memberships: [], configuration: {}, systemId: SYS_LEGACY },
  { id: "20000000-0000-0000-0000-000000000004", name: "Maintenance Access (Weekends)", description: "Weekend only", referenceId: "PLN-MNT", priority: 50, memberships: [], configuration: {}, systemId: SYS_LEGACY }
];

const LOCATIONS: HierarchyItem[] = [
    { id: '10000000-0000-0000-0000-100000000002', groupId: '10000000-0000-0000-0000-100000000001', name: 'Erfurt Office (HQ)', type: 'Location' },
    { id: '40000000-0000-0000-0000-000000000001', groupId: '10000000-0000-0000-0000-100000000001', name: 'Berlin Branch', type: 'Location' },
    { id: '40000000-0000-0000-0000-000000000002', groupId: '10000000-0000-0000-0000-100000000001', name: 'Munich Hub', type: 'Location' },
    { id: '40000000-0000-0000-0000-000000000003', groupId: '10000000-0000-0000-0000-100000000001', name: 'London Office', type: 'Location' },
    { id: '40000000-0000-0000-0000-000000000004', groupId: '10000000-0000-0000-0000-100000000001', name: 'US East (NY)', type: 'Location' },
];

const APPROVAL_GROUPS: HierarchyItem[] = [
     { id: '10000000-0000-0000-0000-200000000002', groupId: '10000000-0000-0000-0000-200000000001', name: 'Facility Management', type: 'Approval' },
     { id: '60000000-0000-0000-0000-000000000001', groupId: '10000000-0000-0000-0000-200000000001', name: 'Security Team', type: 'Approval' },
     { id: '60000000-0000-0000-0000-000000000002', groupId: '10000000-0000-0000-0000-200000000001', name: 'Human Resources', type: 'Approval' },
     { id: '60000000-0000-0000-0000-000000000003', groupId: '10000000-0000-0000-0000-200000000001', name: 'IT Operations', type: 'Approval' },
     { id: '60000000-0000-0000-0000-000000000004', groupId: '10000000-0000-0000-0000-200000000001', name: 'Executive Board', type: 'Approval' },
];

// --- Helpers ---

const SIMULATED_LATENCY_MS = 300;

const delay = <T>(data: T): Promise<T> => {
    return new Promise(resolve => setTimeout(() => resolve(data), SIMULATED_LATENCY_MS));
};

const filterAndPaginate = <T extends { name?: string, id: string }>(
    data: T[], 
    params?: QueryParams
): QueryResult<T> => {
    let result = [...data];
    
    // Filter
    if (params?.filter) {
        const term = params.filter.toLowerCase();
        result = result.filter(item => {
            const name = item.name || '';
            return name.toLowerCase().includes(term) || item.id.toLowerCase().includes(term);
        });
    }

    // Sort
    if (params?.sortBy) {
        result.sort((a, b) => {
            const nameA = a.name || '';
            const nameB = b.name || '';
            return nameA.localeCompare(nameB);
        });
    }

    const total = result.length;

    // Paginate
    if (params?.page !== undefined && params?.pageSize !== undefined) {
        const start = (params.page - 1) * params.pageSize;
        result = result.slice(start, start + params.pageSize);
    }

    return { items: result, total };
};

const batchResolveEntity = <T extends { id: string }>(
    data: T[],
    ids: string[]
): Map<string, T> => {
    const map = new Map<string, T>();
    const uniqueIds = new Set(ids);
    const dataMap = new Map(data.map(i => [i.id, i]));
    
    uniqueIds.forEach(id => {
        const item = dataMap.get(id);
        if (item) map.set(id, item);
    });
    return map;
};

const batchResolveString = (
    data: HierarchyItem[],
    ids: string[]
): Map<string, string> => {
    const map = new Map<string, string>();
    const uniqueIds = new Set(ids);
    data.forEach(item => {
        if (uniqueIds.has(item.id)) {
            map.set(item.id, item.name);
        }
    });
    // Add missing
    uniqueIds.forEach(id => {
        if (!map.has(id)) map.set(id, id); // Fallback to ID
    });
    return map;
}


// --- Implementation ---

export const MockRepository: IRepositories = {
    getSystems: (params) => delay(filterAndPaginate(SYSTEMS, params)),
    getResources: (params) => delay(filterAndPaginate(RESOURCE_DEFS, params)),
    getPlans: (params) => delay(filterAndPaginate(PLANS, params)),
    getLocations: (params) => delay(filterAndPaginate(LOCATIONS, params)),
    getApprovalGroups: (params) => delay(filterAndPaginate(APPROVAL_GROUPS, params)),

    resolveSystems: (ids) => delay(batchResolveEntity(SYSTEMS, ids)),
    resolveResources: (ids) => delay(batchResolveEntity(RESOURCE_DEFS, ids)),
    resolvePlans: (ids) => delay(batchResolveEntity(PLANS, ids)),
    resolveLocations: (ids) => delay(batchResolveString(LOCATIONS, ids)),
};